/**
 * Placeholder rows shown inside UnitsListTable (the top-level "Units" view) while the initial
 * units request is in flight. Rendered within the table's shared Table.Body, so this returns the
 * row fragment only.
 */
import { Box, Table, VisuallyHidden } from '@zillow/constellation-10';

import { LoadingImage, LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import {
  hiddenMobileCellStyles,
  lastRowStyles,
  overflowMenuCellStyle,
  unitCellContentStyles,
  unitInfoStyles,
  unitThumbnailStyles,
} from '../../../styles/propertyCardTable';

type Props = {
  rowCount?: number;
};

const SkeletonUnitListRows = ({ rowCount = PropertiesConstants.PROPERTIES_MULTIPLEX_LIMIT_DEFAULT }: Props) => {
  return Array.from({ length: rowCount }, (_, skeletonIndex) => {
    const isLastSkeletonRow = skeletonIndex === rowCount - 1;

    return (
      <Table.Row
        key={`unit-list-skeleton-${skeletonIndex}`}
        data-testid="UnitsListTable-SkeletonRow"
        css={isLastSkeletonRow ? lastRowStyles : undefined}
      >
        {/* unit */}
        <Table.Cell>
          <Box css={unitCellContentStyles}>
            <Box css={unitThumbnailStyles}>
              <LoadingImage isLoading={true} loadingWidth="100%" loadingHeight="100%" />
            </Box>
            <Box css={unitInfoStyles}>
              <LoadingText textStyle="body-sm-bold" isLoading={true} loadingWidth="96px">
                &nbsp;
              </LoadingText>
              <LoadingText textStyle="body-xs" isLoading={true} loadingWidth="140px">
                &nbsp;
              </LoadingText>
            </Box>
          </Box>
        </Table.Cell>

        {/* address */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <Box css={unitInfoStyles}>
            <LoadingText textStyle="body-sm" isLoading={true} loadingWidth="120px">
              &nbsp;
            </LoadingText>
            <LoadingText textStyle="body-xs" isLoading={true} loadingWidth="140px">
              &nbsp;
            </LoadingText>
          </Box>
        </Table.Cell>

        {/* rent */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <LoadingParagraph textStyle="body-sm" isLoading={true} loadingWidth="60px">
            &nbsp;
          </LoadingParagraph>
        </Table.Cell>

        {/* status */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <LoadingParagraph textStyle="body-sm" isLoading={true} loadingWidth="72px">
            &nbsp;
          </LoadingParagraph>
        </Table.Cell>

        {/* tenants */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <LoadingParagraph textStyle="body-sm" isLoading={true} loadingWidth="100px">
            &nbsp;
          </LoadingParagraph>
        </Table.Cell>

        {/* overflow menu */}
        <Table.Cell css={overflowMenuCellStyle}>
          <VisuallyHidden>Loading</VisuallyHidden>
        </Table.Cell>
      </Table.Row>
    );
  });
};

export default SkeletonUnitListRows;
